/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-product-page.js · the CE parts of the product page's buy area
   (sections/ce-main-product.liquid). Product builder, 09-10-2026. Deferred, no dependencies. assets/ce-product.js
   still runs the gallery, the options and the phone buy bar, and fires `ce:variant:change` { variant } when an
   option changes; this file listens and brings the CE parts in step:
     1. prices BOTH WAYS (ex and inc GST; the page prints both and CSS shows the one the EX / INC GST switch chose):
        the price, the Save badge, the RRP, the GST note, the phone bar
     2. the stock line (the live wording: continue-selling = "In stock", tracked = "N in stock", 1-3 = "Only N left")
     3. finance: SKOPE Funding a day (cents x 0.0008352, rounded once) and SilverChef a week (dollars x .055 x 12 / 52
        in floating point then toFixed(2): the live page's exact cent), shown / hidden by their minimum prices
     4. "Buy more, save more" rows
     5. the Judge.me star badge moves up into the brand row (so the price comes straight after the title); a product
        with no reviews shows no badge there (CSS)
     6. Add to quote: OUR button drives the O: Request a Quote app's own button, which the app injects at run time
        (live A307h: styling the app's own button lost four rounds). Ours shows only once the app's button exists,
        and the app's button is re-found at CLICK time (the app redraws its block).
     7. "Frequently added": Shopify's complementary recommendations, rendered by sections/ce-product-addons.liquid
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEProductPage) return;
  const W = window, D = document;
  const money = (c) => (W.BO && W.BO.money ? W.BO.money(c) : '$' + ((Number(c) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  const exOf = (cents) => Math.round((Number(cents) || 0) * 10 / 11);
  // SilverChef weekly, EXACTLY as the live page: dollars x .055 x 12 / 52 in floating point, then toFixed(2)
  const silverCents = (cents) => Math.round(parseFloat((((Number(cents) || 0) / 100) * 0.055 * 12 / 52).toFixed(2)) * 100);
  const setText = (el, t) => { if (el && el.textContent !== t) el.textContent = t; };
  const each = (root, sel, fn) => root.querySelectorAll(sel).forEach(fn);

  /* ---------- 1-4: one section, one variant ---------- */
  const paint = (root, v) => {
    if (!v) return;
    const inc = Number(v.price) || 0;
    const ex = exOf(inc);
    const was = Number(v.compare_at_price) || 0;
    const sale = was > inc;
    each(root, '[data-cpp-inc], [data-cpp-inc2], [data-cpp-bar-inc]', (el) => setText(el, money(inc)));
    each(root, '[data-cpp-ex], [data-cpp-ex2], [data-cpp-bar-ex]', (el) => setText(el, money(ex)));
    // the bar lives on <body> (ce-product.js moves it there)
    const sid = root.getAttribute('data-section');
    D.querySelectorAll('body > [data-bop-bar="' + sid + '"]').forEach((bar) => {
      each(bar, '[data-cpp-bar-inc]', (el) => setText(el, money(inc)));
      each(bar, '[data-cpp-bar-ex]', (el) => setText(el, money(ex)));
    });
    each(root, '[data-cpp-deal]', (el) => { el.hidden = !sale; });
    each(root, '[data-cpp-price]', (el) => el.classList.toggle('is-sale', sale));
    if (sale) {
      const wasEx = exOf(was);
      each(root, '[data-cpp-save-inc]', (el) => setText(el, money(was - inc)));
      each(root, '[data-cpp-save-ex]', (el) => setText(el, money(wasEx - ex)));
      each(root, '[data-cpp-was-inc]', (el) => setText(el, money(was)));
      each(root, '[data-cpp-was-ex]', (el) => setText(el, money(wasEx)));
      // Tommy 10-10: the saving as a percentage too, "Save $X (Y%)" - rounded like the cards' "-Y%" badge
      const pct = Math.round(((was - inc) * 100) / was);
      each(root, '[data-cpp-save-pct]', (el) => setText(el, String(pct)));
      each(root, '[data-cpp-save-pct-wrap]', (el) => { el.hidden = pct < 1; });
    }
    // stock
    each(root, '[data-cpp-stock]', (el) => {
      const low = parseInt(el.getAttribute('data-low'), 10) || 3;
      let cls = 'in', words = el.getAttribute('data-t-in') || 'In stock';
      const q = parseInt(v.qty, 10) || 0;
      if (!v.available) { cls = 'out'; words = el.getAttribute('data-t-out') || 'Out of stock'; }
      else if (!v.tracked || v.policy === 'continue') { words = el.getAttribute('data-t-in') || 'In stock'; }
      else if (q > low) { words = (el.getAttribute('data-t-qty') || '[count] in stock').replace('[count]', String(q)); }
      else { cls = 'low'; words = (el.getAttribute('data-t-low') || 'Only [count] left').replace('[count]', String(q)); }
      el.className = 'cpp-stock cpp-stock--' + cls;
      setText(el.querySelector('[data-cpp-stock-text]'), words);
    });
    // finance
    each(root, '[data-cfin]', (box) => {
      const skMin = parseInt(box.getAttribute('data-skope-min'), 10) || 0;
      const scMin = parseInt(box.getAttribute('data-sc-min'), 10) || 0;
      const sk = box.querySelector('[data-cfin-skope]');
      if (sk) {
        sk.hidden = inc < skMin;
        const c = Math.round(inc * 0.0008352);
        setText(sk.querySelector('[data-cfin-skope-amt]'), Math.floor(c / 100) + '.' + String(c % 100).padStart(2, '0'));
      }
      const sc = box.querySelector('[data-cfin-sc]');
      if (sc) {
        // 02-11-2026: shown from $1,000; under $1,800 (a NEW SilverChef customer's minimum) the "existing customer" words
        const scNew = parseInt(box.getAttribute('data-sc-new'), 10) || 0;
        const tier = inc >= scNew ? 'new' : 'existing';
        sc.hidden = !(inc > 0 && inc >= scMin);
        sc.setAttribute('data-price', String(inc));
        setText(sc.querySelector('[data-cfin-sc-amt]'), money(silverCents(inc)));
        each(sc, '[data-cfin-sc-tier]', (el) => { el.hidden = el.getAttribute('data-cfin-sc-tier') !== tier; });
      }
    });
    // tiers
    each(root, '[data-ctr] [data-ctr-q]', (row) => {
      const pct = parseFloat(row.getAttribute('data-ctr-pct')) || 0;
      const eachInc = Math.round(inc * (100 - pct) / 100);
      setText(row.querySelector('[data-ctr-inc]'), money(eachInc));
      setText(row.querySelector('[data-ctr-ex]'), money(exOf(eachInc)));
    });
  };

  /* ---------- 5: the star badge goes up into the brand row ---------- */
  const moveStars = (root) => {
    const slot = root.querySelector('[data-cpp-stars-slot]');
    if (!slot) return;
    root.querySelectorAll('[data-cpp-app]').forEach((app) => {
      if (app.parentElement === slot) return;
      if (app.querySelector('[id*="preview_badge"], .jdgm-preview-badge, .jdgm-prev-badge')) slot.appendChild(app);
    });
  };

  /* ---------- 6: Add to quote ---------- */
  // The app's markup (measured 09-10): .omgrfq-button > .omgrfq-product-button.omgrfq-btn-add-quotes >
  // .omg-button-quote-onboarding > button.omgrfq-btn-quotes. Its handler sits on the inner BUTTON (a click on the
  // wrapper div never reaches it: events bubble up, not down), so the button is what we press.
  const appQuoteBtn = (root) => {
    // 14-10-2026: Quotes AI replaces Omega. THIS button stays the one shoppers see - full width under Add to cart, the
    // theme's own outline and icon (Tommy, the minute the app's went live: "needs to look like it did a few moments
    // ago") - and the app's own button ([data-qai-add]) is hidden by ce-product-page.css and pressed through here,
    // exactly as Omega's was. Not when the app itself has hidden it (a rule that says no quote for this shopper).
    const qai = root.querySelector('[data-qai-add]') || document.querySelector('[data-qai-add]');
    if (qai) return (qai.hidden || qai.closest('.qai-hide') || qai.closest('[hidden]')) ? null : qai;
    const scope = root.querySelector('[data-cpp-buyrow]') || root;
    let el = scope.querySelector('.omgrfq-button button.omgrfq-btn-quotes, .omgrfq-button button, .omgrfq-button a');
    if (el) return el;
    el = root.querySelector('button.omgrfq-btn-quotes, .omgrfq-product-button button');
    if (el) return el;
    // last resort: the label, never our own proxy
    const all = root.querySelectorAll('button, a');
    for (let i = 0; i < all.length; i++) {
      const tx = (all[i].textContent || '').trim().toLowerCase();
      if (tx === 'add to quote' && !all[i].hasAttribute('data-cpp-quote')) return all[i];
    }
    return null;
  };
  const bindQuote = (root) => {
    const ours = root.querySelector('[data-cpp-quote]');
    if (!ours || ours.cppBound) return;
    ours.cppBound = true;
    const said = root.querySelector('[data-cpp-quote-said]');
    const label = ours.querySelector('[data-cpp-quote-text]');
    const base = label ? label.textContent : 'Add to quote';
    // the app draws late: keep looking for a while, then give up quietly (a dead button is worse than none)
    let tries = 0;
    const look = () => {
      const real = appQuoteBtn(root);
      if (real) {
        ours.hidden = false;
        root.classList.add('cpp--has-quote');
        return;
      }
      if (++tries < 60) setTimeout(look, 250);
    };
    look();
    ours.addEventListener('click', (e) => {
      e.preventDefault();
      const real = appQuoteBtn(root);           // re-found at click time: the app redraws its block
      if (!real) return;
      real.click();
      ours.classList.add('is-added');
      if (label) label.textContent = 'Added to quote';
      // the app opens its own quote window (Step 1 products, Step 2 contact info): no second message from us
      if (said) said.hidden = true;
      clearTimeout(ours.cppT);
      ours.cppT = setTimeout(() => { ours.classList.remove('is-added'); if (label) label.textContent = base; }, 2600);
    });
  };

  /* ---------- 7: Frequently added ---------- */
  const loadAddons = (root) => {
    root.querySelectorAll('[data-cpp-addons][data-url]').forEach((box) => {
      if (box.cppBound) return;
      box.cppBound = true;
      const go = () => fetch(box.getAttribute('data-url'), { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('.cpa');
          if (!list || !list.querySelector('.cpa__row')) return;
          const slot = box.querySelector('[data-cpp-addons-slot]');
          if (!slot) return;
          slot.innerHTML = '';
          slot.appendChild(list);
          box.hidden = false;
        })
        .catch(() => { /* nothing to add: the block stays away */ });
      if ('requestIdleCallback' in W) W.requestIdleCallback(go, { timeout: 2500 }); else setTimeout(go, 600);
    });
  };

  /* ---------- bind ---------- */
  const bind = (root) => {
    if (root.cppBound) return;
    root.cppBound = true;
    moveStars(root);
    bindQuote(root);
    loadAddons(root);
    // SilverChef: the page printed a cent rounded by Liquid; put the live page's exact figure in
    root.querySelectorAll('[data-cfin-sc]').forEach((sc) => {
      const c = parseInt(sc.getAttribute('data-price'), 10);
      if (c > 0) setText(sc.querySelector('[data-cfin-sc-amt]'), money(silverCents(c)));
    });
    root.addEventListener('ce:variant:change', (e) => paint(root, e.detail && e.detail.variant));
    // a late Judge.me redraw: one more look once everything has loaded
    W.addEventListener('load', () => moveStars(root), { once: true });
  };
  const init = (scope) => {
    const r = scope && scope.querySelectorAll ? scope : D;
    r.querySelectorAll('[data-cpp]').forEach(bind);
    if (r.matches && r.matches('[data-cpp]')) bind(r);
  };
  /* the details tabs: a long description opens shortened, "Read the full description" opens it (live read-more) */
  const bindMore = (scope) => {
    (scope || D).querySelectorAll('[data-cps-more]').forEach((wrap) => {
      if (wrap.cpsBound) return;
      wrap.cpsBound = true;
      const body = wrap.querySelector('[data-cps-more-body]');
      const btn = wrap.querySelector('[data-cps-more-btn]');
      if (!body || !btn) return;
      const limit = W.matchMedia('(max-width: 749px)').matches ? 520 : 640;
      if (body.scrollHeight <= limit + 120) return;          // short enough: shown whole, no button
      wrap.classList.add('is-clamped');
      btn.hidden = false;
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', () => {
        wrap.classList.remove('is-clamped');
        btn.hidden = true;
        btn.setAttribute('aria-expanded', 'true');
      });
    });
  };
  /* Tommy 12-10 (the Hoshizaki flake ice description: "unique ice production section??"): a description picture that
     will not load is taken out, with the empty paragraph it sat in and a heading left with nothing under it. 22 live
     descriptions had one - an old hoshizaki.com.au file (404) or a "{{media url=" leftover from the old site. */
  const gone = (el) => { el.style.display = 'none'; el.setAttribute('data-cps-gone', ''); };
  const shown = (el) => el && !el.hasAttribute('data-cps-gone');
  const dropImg = (img) => {
    if (!shown(img)) return;
    const body = img.closest('[data-cps-more-body]');
    gone(img);
    let after = img.nextSibling;
    while (after && after.nodeType === 3 && !after.textContent.trim()) after = after.nextSibling;
    if (after && after.tagName === 'BR') gone(after);
    let top = img;
    for (let p = img.parentElement; p && p !== body; p = p.parentElement) {
      if (p.textContent.trim() || [...p.querySelectorAll('img, iframe, video')].some(shown)) break;
      gone(p);
      top = p;
    }
    if (top === img || top.parentElement !== body) return;
    let next = top.nextElementSibling;
    while (next && !shown(next)) next = next.nextElementSibling;
    const head = top.previousElementSibling;
    if (head && /^H[1-6]$/.test(head.tagName) && (!next || /^H[1-6]$/.test(next.tagName))) gone(head);
  };
  const guardImgs = (scope) => {
    (scope || D).querySelectorAll('[data-cps-more-body] img').forEach((img) => {
      if (img.complete && !img.naturalWidth) dropImg(img);
      else img.addEventListener('error', () => dropImg(img), { once: true });
    });
  };
  guardImgs(D);
  D.addEventListener('shopify:section:load', (e) => guardImgs(e.target));
  const initMore = () => bindMore(D);
  if (D.readyState === 'complete') initMore(); else W.addEventListener('load', initMore, { once: true });
  D.addEventListener('shopify:section:load', (e) => bindMore(e.target));

  /* Tommy 11-10 ("after the add to quote button there is too much scroll to reach the product description ... should
     the promise and fit check come after the at a glance section?"): on phones and narrow tablets The CEO Promise and
     Fit Check ([data-cpp-phone-move]) move from under the buy buttons to the details section's slot, after
     "At a glance" ([data-cpp-phone-slot]); back again on a wider screen. They sit far below the first screen, so the
     move never shifts what is being looked at. Each finance card's Details toggle opens its small print (phones). */
  // Tommy 12-10 ("I think the CEO promise and fit check should be down below the description etc. - on desktop too,
  // not just mobile"): every screen width now, not only phones and narrow tablets (was max-width 899px).
  const phone = W.matchMedia('all');
  const placeBlocks = () => {
    const slot = D.querySelector('[data-cpp-phone-slot]');
    D.querySelectorAll('[data-cpp-phone-move]').forEach((el) => {
      if (!el.cppHome) { el.cppHome = D.createComment('cpp-phone-move'); el.parentNode.insertBefore(el.cppHome, el); }
      if (phone.matches && slot) { if (el.parentNode !== slot) slot.appendChild(el); }
      else if (el.previousSibling !== el.cppHome) el.cppHome.parentNode.insertBefore(el, el.cppHome.nextSibling);
    });
  };
  const bindFinance = (scope) => {
    (scope || D).querySelectorAll('[data-cfin-more]').forEach((b) => {
      if (b.cfinBound) return;
      b.cfinBound = true;
      b.addEventListener('click', () => {
        const box = b.closest('.cfin__box');
        const open = !box.classList.contains('is-open');
        box.classList.toggle('is-open', open);
        b.setAttribute('aria-expanded', String(open));
      });
    });
  };
  const initPhone = () => { placeBlocks(); bindFinance(D); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', initPhone); else initPhone();
  if (phone.addEventListener) phone.addEventListener('change', placeBlocks);
  D.addEventListener('shopify:section:load', (e) => { placeBlocks(); bindFinance(e.target); });

  W.CEProductPage = { init, paint };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
